/**
 * `shortId` — публичный короткий алиас товара для share-ссылок карточки
 * (ADR-0095, Фаза A2). Формат `<prefix><share_hash>`, где `<prefix>` кодирует
 * домен каталога (= ключ `/s/<k>` будущего резолв-эндпоинта Фазы B), а
 * `<share_hash>` — детерминированный {@link stableShareHash} от verbatim
 * Global_ID (см. `./stable-share-hash`).
 *
 * Концерн API-ЭКСПОЗИЦИИ: навешивается на границе API (edge repository + MSW),
 * НЕ в `catalog-db` `rowToX` (маппинг остаётся чистым; roundtrip-тесты A1 не
 * меняются). `shortId` — производное от verbatim Global_ID (P-001): Global_ID
 * не нормализуется, поэтому алиас стабилен.
 *
 * Фаза B переиспользует {@link SHORT_ID_PREFIX} для обратного parse `<prefix>` →
 * домен — префиксы определены здесь один раз, не дублируются.
 */

export const SHORT_ID_PREFIX = { decors: 'd', handles: 'h', furniture: 'f' } as const;
export type ShortIdDomain = keyof typeof SHORT_ID_PREFIX;

export function buildShortId(domain: ShortIdDomain, shareHash: string): string {
  return `${SHORT_ID_PREFIX[domain]}${shareHash}`;
}

/** Навешивает optional shortId на товар. shareHash null → shortId не добавляется. */
export function withShortId<T extends { globalId: string }>(
  item: T,
  domain: ShortIdDomain,
  shareHash: string | null,
): T & { shortId?: string } {
  return { ...item, shortId: shareHash ? buildShortId(domain, shareHash) : undefined };
}

/**
 * Обратное отображение `<prefix>` → домен (Фаза B резолв `/s/<id>`). Строится из
 * того же {@link SHORT_ID_PREFIX} — единственный источник префиксов, не дублируем.
 */
const PREFIX_TO_DOMAIN = Object.fromEntries(
  Object.entries(SHORT_ID_PREFIX).map(([d, p]) => [p, d]),
) as Record<string, ShortIdDomain>;

/** `<prefix><shareHash>` → { domain, shareHash } либо null (неизвестный префикс / пустой shareHash). */
export function parseShortId(id: string): { domain: ShortIdDomain; shareHash: string } | null {
  const domain = PREFIX_TO_DOMAIN[id[0] ?? ''];
  const shareHash = id.slice(1);
  return domain && shareHash ? { domain, shareHash } : null;
}
